import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { classAccent, CLASS_COLORS } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, ArrowUp, ArrowDown, ArrowLeft, Check } from "lucide-react";
import { ClassPreview } from "./ClassPreview";

const META_KEYS = ["title", "description", "theme", "color"];
const metaOf = (t) => Object.fromEntries(META_KEYS.map((k) => [k, t?.[k] || ""]));

const run = async (fn, after) => { try { await fn(); after?.(); } catch (e) { toast.error(e.message); } };
const saveBtn = "h-9 px-3 rounded-lg border-2 border-ink text-xs font-bold press shrink-0";
const iconBtn = "h-9 w-9 grid place-items-center rounded-lg border-2 border-ink press shrink-0";

function ClassList({ classes, onOpen, onDelete }) {
  return (
    <div data-testid="content-tab">
      <p className="text-muted-foreground mb-4 text-sm">Pick a class to edit its weeks, days and questions.</p>
      <div className="grid gap-4 sm:grid-cols-3">
        {classes.map((c, i) => (
          <div key={c.id} className="relative rounded-3xl border-2 border-ink bg-white shadow-pop overflow-hidden">
            <button onClick={() => onOpen(c.id)} data-testid={`content-class-${c.slug}`} className="text-left w-full press p-6 pr-14">
              <div className="h-2 w-12 rounded-full mb-3" style={{ background: classAccent(c.slug, i, c.color).bg }} />
              <div className="text-xl font-extrabold">{c.title}</div>
              {c.theme && <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mt-0.5">{c.theme}</div>}
              <div className="text-xs text-muted-foreground mt-1">{c.week_count} weeks · {c.day_count} days</div>
            </button>
            <button onClick={() => onDelete(c)} data-testid={`delete-class-${c.slug}`} title="Delete class"
              className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-xl border-2 border-ink press"
              style={{ background: "#FF3B7A", color: "#FFFDF5" }}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClassMetaForm({ tree, patchTree, onSave }) {
  return (
    <div className="rounded-3xl border-2 border-ink bg-white shadow-pop p-6">
      <Label>Class title</Label>
      <Input value={tree.title || ""} onChange={(e) => patchTree((c) => { c.title = e.target.value; })} className="border-2 border-ink h-11 rounded-xl mt-1 mb-3 font-bold" data-testid="edit-class-title" />
      <Label>Description</Label>
      <Textarea value={tree.description || ""} onChange={(e) => patchTree((c) => { c.description = e.target.value; })} rows={2} className="border-2 border-ink rounded-xl mt-1 mb-3 resize-none" />
      <Label>Theme pill</Label>
      <Input value={tree.theme || ""} onChange={(e) => patchTree((c) => { c.theme = e.target.value; })} placeholder='e.g. "The Bible"' className="border-2 border-ink h-11 rounded-xl mt-1 mb-3" data-testid="edit-class-theme" />
      <Label>Color</Label>
      <div className="flex gap-2 mt-1 mb-4" data-testid="edit-class-color">
        {Object.entries(CLASS_COLORS).map(([key, c]) => {
          const selected = (tree.color || "") === key;
          return (
            <button key={key} type="button" onClick={() => patchTree((t) => { t.color = key; })}
              data-testid={`color-swatch-${key}`} title={key} aria-label={key}
              className={`h-10 w-10 grid place-items-center rounded-xl border-2 press ${selected ? "border-ink ring-2 ring-offset-1 ring-ink" : "border-ink/30"}`}
              style={{ background: c.bg }}>
              {selected && <Check size={16} style={{ color: c.fg }} />}
            </button>
          );
        })}
      </div>
      <Button onClick={onSave} className="rounded-full border-2 border-ink press font-bold" data-testid="save-class">Save class</Button>
    </div>
  );
}

function QuestionRow({ q, onChange, reload }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[10px] font-bold uppercase w-16 shrink-0 text-muted-foreground">{q.type.replace("_", " ")}</span>
      <Input value={q.text} onChange={(e) => onChange(e.target.value)} className="border-2 border-ink h-9 rounded-lg text-sm" />
      <button onClick={() => run(async () => { await apiPatch(`/admin/questions/${q.id}`, { text: q.text }); toast.success("Saved"); })} className="h-9 px-2 rounded-lg border-2 border-ink text-xs font-bold press shrink-0" style={{ background: "#B8FF3B" }}>Save</button>
      <button onClick={() => run(() => apiPost(`/admin/questions/${q.id}/reorder`, { direction: "up" }), reload)} className="h-9 w-8 grid place-items-center rounded-lg border-2 border-ink press shrink-0"><ArrowUp size={13} /></button>
      <button onClick={() => run(() => apiPost(`/admin/questions/${q.id}/reorder`, { direction: "down" }), reload)} className="h-9 w-8 grid place-items-center rounded-lg border-2 border-ink press shrink-0"><ArrowDown size={13} /></button>
      <button onClick={() => run(() => apiDelete(`/admin/questions/${q.id}`), reload)} className="h-9 w-8 grid place-items-center rounded-lg border-2 border-ink press shrink-0"><Trash2 size={13} /></button>
    </div>
  );
}

function DayEditor({ d, edit, reload }) {
  const save = () => run(async () => { await apiPatch(`/admin/days/${d.id}`, { title: d.title, scripture_reference: d.scripture_reference, devotional: d.devotional }); toast.success("Day saved"); });
  const addQ = (type) => run(() => apiPost(`/admin/days/${d.id}/questions`, { text: "New question", type }), reload);
  return (
    <div className="rounded-2xl border-2 border-ink p-4" data-testid={`edit-day-${d.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-7 w-7 grid place-items-center rounded-full border-2 border-ink text-xs font-bold shrink-0">{d.day_number}</span>
        <Input value={d.title || ""} onChange={(e) => edit((x) => { x.title = e.target.value; })} className="border-2 border-ink h-9 rounded-lg font-bold" />
        <button onClick={save} className={saveBtn} style={{ background: "#B8FF3B" }}>Save</button>
        <button onClick={() => run(() => apiDelete(`/admin/days/${d.id}`), reload)} className={iconBtn}><Trash2 size={14} /></button>
      </div>
      <Input value={d.scripture_reference || ""} onChange={(e) => edit((x) => { x.scripture_reference = e.target.value; })} className="border-2 border-ink h-9 rounded-lg mb-2 text-sm" placeholder="Scripture reference" />
      <Textarea value={d.devotional || ""} onChange={(e) => edit((x) => { x.devotional = e.target.value; })} rows={3} className="border-2 border-ink rounded-lg resize-none text-sm mb-3" placeholder="Devotional" />
      {d.questions.map((q, qi) => (
        <QuestionRow key={q.id} q={q} reload={reload} onChange={(v) => edit((x) => { x.questions[qi].text = v; })} />
      ))}
      <div className="flex gap-2 mt-2">
        <button onClick={() => addQ("pre_reading")} className="text-xs font-bold px-3 py-1.5 rounded-full border-2 border-ink press"><Plus size={12} className="inline mr-1" />Pre-reading</button>
        <button onClick={() => addQ("reflection")} className="text-xs font-bold px-3 py-1.5 rounded-full border-2 border-ink press"><Plus size={12} className="inline mr-1" />Reflection</button>
      </div>
    </div>
  );
}

function WeekEditor({ w, edit, reload }) {
  const save = () => run(async () => { await apiPatch(`/admin/weeks/${w.id}`, { title: w.title, intro_text: w.intro_text, opening_prayer: w.opening_prayer, summary_text: w.summary_text }); toast.success("Week saved"); });
  const del = () => { if (window.confirm("Delete this week and all its days?")) run(() => apiDelete(`/admin/weeks/${w.id}`), reload); };
  const addDay = () => run(() => apiPost(`/admin/weeks/${w.id}/days`, { title: "New day", scripture_reference: "", devotional: "" }), reload);
  return (
    <div className="rounded-3xl border-2 border-ink bg-white overflow-hidden" data-testid={`edit-week-${w.id}`}>
      <div className="px-5 py-4 border-b-2 border-ink" style={{ background: "#FFFDF5" }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground shrink-0">Week {w.week_number}</span>
          <Input value={w.title || ""} onChange={(e) => edit((x) => { x.title = e.target.value; })} className="border-2 border-ink h-9 rounded-lg font-bold" />
          <button onClick={save} className={saveBtn} style={{ background: "#B8FF3B" }}>Save</button>
          <button onClick={del} className={iconBtn}><Trash2 size={14} /></button>
        </div>
        <Textarea value={w.opening_prayer || ""} onChange={(e) => edit((x) => { x.opening_prayer = e.target.value; })} rows={2} className="border-2 border-ink rounded-lg resize-none text-sm" placeholder="Opening prayer" />
      </div>
      <div className="p-5 space-y-4">
        {w.days.map((d, di) => (
          <DayEditor key={d.id} d={d} reload={reload} edit={(fn) => edit((x) => fn(x.days[di]))} />
        ))}
        <button onClick={addDay} className="w-full py-2 rounded-xl border-2 border-dashed border-ink text-sm font-bold press"><Plus size={14} className="inline mr-1" />Add day</button>
      </div>
    </div>
  );
}

export function ContentTab() {
  const [classes, setClasses] = useState([]);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedMeta, setSavedMeta] = useState(null);

  const loadClasses = useCallback(() => { apiGet("/admin/classes").then(setClasses).catch((e) => toast.error(e.message)); }, []);
  useEffect(() => { loadClasses(); }, [loadClasses]);

  const openClass = async (id) => {
    setLoading(true);
    try { const t = await apiGet(`/admin/classes/${id}/tree`); setTree(t); setSavedMeta(metaOf(t)); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  const reload = () => tree && openClass(tree.id);

  const deleteClass = (c) => {
    if (!window.confirm(`Permanently delete "${c.title}" and all its weeks, days, questions and student progress? This cannot be undone.`)) return;
    run(async () => { await apiDelete(`/admin/classes/${c.id}`); toast.success(`"${c.title}" deleted`); }, loadClasses);
  };
  const saveClass = () => run(async () => {
    await apiPatch(`/admin/classes/${tree.id}`, { title: tree.title, description: tree.description, color: tree.color || "", theme: tree.theme || "" });
    setSavedMeta(metaOf(tree));
    toast.success("Class saved");
  }, loadClasses);
  const addWeek = () => run(() => apiPost(`/admin/classes/${tree.id}/weeks`, { title: "New week", intro_text: "", opening_prayer: "", summary_text: "" }), reload);

  const patchTree = (fn) => setTree((t) => { const c = JSON.parse(JSON.stringify(t)); fn(c); return c; });

  if (!tree) return <ClassList classes={classes} onOpen={openClass} onDelete={deleteClass} />;

  return (
    <div data-testid="content-editor">
      <button onClick={() => setTree(null)} className="flex items-center gap-1 text-sm font-bold mb-4 press"><ArrowLeft size={15} /> All classes</button>
      {loading && <div className="text-sm text-muted-foreground mb-2">Loading…</div>}
      <div className="grid lg:grid-cols-[1fr_420px] gap-5 mb-5">
        <ClassMetaForm tree={tree} patchTree={patchTree} onSave={saveClass} />
        <ClassPreview cls={tree} dirty={JSON.stringify(metaOf(tree)) !== JSON.stringify(savedMeta)} />
      </div>
      <div className="space-y-4">
        {tree.weeks.map((w, wi) => (
          <WeekEditor key={w.id} w={w} reload={reload} edit={(fn) => patchTree((c) => fn(c.weeks[wi]))} />
        ))}
        <button onClick={addWeek} data-testid="add-week" className="w-full py-3 rounded-2xl border-2 border-dashed border-ink font-bold press"><Plus size={16} className="inline mr-1" />Add week</button>
      </div>
    </div>
  );
}
